import { useState } from "react";
import { useDispatch } from "react-redux";
import { useToggle } from "usehooks-ts";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";

import DataTable from "components/DataTable";
import { LOAD_BID_HEADER_LIST } from "constants/loads";
import { ILoadBid } from "models/Load";
import BidInfo from "../BidInfo";
import ConfirmationModal from "components/ConfirmationModal";
import useDeviceType from "hooks/useDeviceType";
import { handleLoading } from "store/global/loader";
import { showErrorMessage } from "helpers/toastMessage";

import { AppDispatch } from "store";

import { MUTATION_ACCEPT_BID } from "../../Detail/query";
import { parseError } from "helpers/convertFormData";

import { routes } from "constants/routes";

export default function BidTableDetails({
  bids,
  refresh,
  isDeleted
}: {
  bids: ILoadBid[];
  refresh?: VoidFunction;
  isDeleted: boolean;
}) {
  const { isDesktop } = useDeviceType();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [acceptBidId, setAcceptBidId] = useState("");
  const [isAcceptBidModalOpen, toggleAcceptBidModalOpen] = useToggle();

  const [AcceptBid] = useMutation(MUTATION_ACCEPT_BID, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const goToUserProfile = (id: string) => {
    navigate(routes.USER + `/${id}`);
  };

  const acceptBid = async () => {
    try {
      dispatch(handleLoading(true));
      await AcceptBid({
        variables: {
          bidId: acceptBidId
        }
      });
      refresh && refresh();
      toggleAcceptBidModalOpen();
    } catch (error) {
      showErrorMessage("Failed to accept bid.");
    } finally {
      dispatch(handleLoading(false));
    }
  };
  return (
    <>
      <div className="mobile:flex mobile:flex-col mobile:gap-3 mobile:text-sm">
        {isDesktop ? (
          <DataTable
            headers={LOAD_BID_HEADER_LIST}
            data={bids.map((bid: ILoadBid) => ({
              ...bid,
              currentBid: {
                isDeleted: isDeleted,
                created: bid.created,
                amount: bid.amount,
                bidderNotes: bid.bidderNotes,
                bidStatus: bid.bidStatus,
                toggleAcceptBidModalOpen: () => {
                  setAcceptBidId(bid.id);
                  toggleAcceptBidModalOpen();
                },
                goToUserProfile: () => goToUserProfile(bid.user.id)
              },
              reporting: {
                averageRating: bid.user?.averageRating ?? 0,
                goToUserProfile: () => goToUserProfile(bid.user.id)
              }
            }))}
          />
        ) : (
          <>
            {bids.map((bid: ILoadBid) => (
              <BidInfo
                key={bid.id}
                isDeleted={isDeleted}
                bid={bid}
                goToUserProfile={() => goToUserProfile(bid.user.id)}
                setAcceptBidId={setAcceptBidId}
                toggleAcceptBidModalOpen={toggleAcceptBidModalOpen}
              />
            ))}
          </>
        )}

        <ConfirmationModal
          content={"Are you sure you want to accept this bid?"}
          isConfirmModalOpen={isAcceptBidModalOpen}
          toggleConfirmModal={toggleAcceptBidModalOpen}
          onOK={acceptBid}
        />
      </div>
    </>
  );
}
